import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ColumnDef, SortingState } from '@tanstack/react-table'
import { DataTable, SortableHeader, FilterableHeader, createSelectionColumn } from '../ui/data-table'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Trash2, Languages, Eye, EyeOff, Edit, AlertCircle, BookImage, Sparkles, Plus, FileText, RefreshCw, Upload } from 'lucide-react'
import { apiClient } from '../../lib/api'
import { useScrollRestoration } from '../../hooks/useScrollRestoration'
import { formatDateTimeCompact } from '../../lib/utils'
import { ALL_LANGUAGES } from '../../lib/constants/languages'

// Language codes sorted alphabetically by name
const LANGUAGE_CODES = [...ALL_LANGUAGES]
  .sort((a, b) => a.name.localeCompare(b.name))
  .map(l => l.code)

// Book visibility statuses
const BOOK_STATUSES = ['visible', 'hidden']
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import { BulkTranslationDialog } from './BulkTranslationDialog'
import { GenerateCoverDialog } from './GenerateCoverDialog'
import { GenerateDescriptionDialog } from './GenerateDescriptionDialog'
import { GenerateDescriptionCoverDialog } from './GenerateDescriptionCoverDialog'

interface Book {
  id: string
  title_international: string
  author_international: string
  original_language: string | null
  genre: string | null
  description: string | null
  is_hidden: boolean
  created_at: string
  updated_at: string
  translation_count?: number
  cover_url?: string | null
  estimated_pages?: number | null
}

interface BooksTableProps {
  isActive?: boolean
}

const BOOKS_PAGE_SIZE_KEY = 'admin-books-page-size'

export function BooksTable({ isActive = true }: BooksTableProps) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  useScrollRestoration('admin-books-table', isActive)
  const [books, setBooks] = useState<Book[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedBooks, setSelectedBooks] = useState<Book[]>([])
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showTranslationDialog, setShowTranslationDialog] = useState(false)
  const [showGenerateCoverDialog, setShowGenerateCoverDialog] = useState(false)
  const [showGenerateDescriptionDialog, setShowGenerateDescriptionDialog] = useState(false)
  const [showGenerateDescriptionCoverDialog, setShowGenerateDescriptionCoverDialog] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [recalculatingPages, setRecalculatingPages] = useState(false)
  const [recalculatingSingleBook, setRecalculatingSingleBook] = useState<string | null>(null)
  
  // Load initial pageSize from localStorage
  const getInitialPageSize = () => {
    const saved = localStorage.getItem(BOOKS_PAGE_SIZE_KEY)
    return saved ? parseInt(saved, 10) : 10
  }

  // Load initial pageIndex from URL
  const getInitialPageIndex = () => {
    const pageParam = searchParams.get('page')
    return pageParam ? Math.max(0, parseInt(pageParam, 10) - 1) : 0
  }
  
  const [pagination, setPagination] = useState(() => ({ 
    pageIndex: getInitialPageIndex(), 
    pageSize: getInitialPageSize() 
  }))
  const [pageCount, setPageCount] = useState(0)
  
  // Server-side filtering, sorting, and search state
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([])
  const [selectedGenres, setSelectedGenres] = useState<string[]>([])
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([])
  const [availableGenres, setAvailableGenres] = useState<string[]>([])
  const [sorting, setSorting] = useState<SortingState>([{ id: 'created_at', desc: true }])
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Use a ref to track previous pagination to avoid unnecessary fetches
  const prevPaginationRef = useRef({ pageIndex: getInitialPageIndex(), pageSize: getInitialPageSize() })
  
  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchValue])
  
  // Load genres on mount
  useEffect(() => {
    const loadGenres = async () => {
      try {
        const data = await apiClient.getAdminGenres()
        setAvailableGenres(data.genres || [])
      } catch (error) {
        console.error('Failed to load genres:', error)
      }
    }
    loadGenres()
  }, [])
  
  // Save pageSize to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(BOOKS_PAGE_SIZE_KEY, String(pagination.pageSize))
  }, [pagination.pageSize])

  // Sync pageIndex to URL
  useEffect(() => {
    const currentPage = pagination.pageIndex + 1
    const urlPage = searchParams.get('page')
    
    if (currentPage === 1) {
      // Remove page param if on first page
      if (urlPage) {
        searchParams.delete('page')
        setSearchParams(searchParams, { replace: true })
      }
    } else if (urlPage !== String(currentPage)) {
      searchParams.set('page', String(currentPage))
      setSearchParams(searchParams, { replace: true })
    }
  }, [pagination.pageIndex, searchParams, setSearchParams])

  useEffect(() => {
    // Only load if pagination values actually changed
    if (
      prevPaginationRef.current.pageIndex !== pagination.pageIndex ||
      prevPaginationRef.current.pageSize !== pagination.pageSize
    ) {
      prevPaginationRef.current = pagination
      loadBooks()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination])

  // Load books on initial mount
  useEffect(() => {
    loadBooks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  
  // Reload when filters, sorting, or search change
  useEffect(() => {
    // Reset to first page when filters change
    if (pagination.pageIndex !== 0) {
      setPagination(prev => ({ ...prev, pageIndex: 0 }))
    } else {
      loadBooks()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguages, selectedGenres, selectedStatuses, sorting, debouncedSearch])

  const loadBooks = useCallback(async () => {
    try {
      setLoading(true)
      const page = pagination.pageIndex + 1 // API uses 1-based pages
      
      // Build sort options from sorting state
      const sortBy = sorting.length > 0 ? sorting[0].id : 'created_at'
      const sortOrder = sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : 'desc'
      
      const data = await apiClient.getAdminBooks(true, page, pagination.pageSize, {
        languages: selectedLanguages.length > 0 ? selectedLanguages : undefined,
        genres: selectedGenres.length > 0 ? selectedGenres : undefined,
        statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        search: debouncedSearch || undefined,
        sortBy,
        sortOrder,
      })
      setBooks(data.books)
      if (data.pagination) {
        setPageCount(data.pagination.total_pages)
      }
    } catch (error) {
      console.error('Failed to load books:', error)
    } finally {
      setLoading(false)
    }
  }, [pagination.pageIndex, pagination.pageSize, selectedLanguages, selectedGenres, selectedStatuses, sorting, debouncedSearch])

  const handleToggleVisibility = useCallback(async (e: React.MouseEvent, bookId: string, currentlyHidden: boolean) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      await apiClient.toggleBookHidden(bookId, !currentlyHidden)
      await loadBooks()
    } catch (error) {
      console.error('Failed to toggle book visibility:', error)
      alert('Failed to update book visibility')
    }
  }, [])

  const handleDeleteSelected = async () => {
    if (selectedBooks.length === 0) return

    setDeleting(true)
    try {
      await Promise.all(
        selectedBooks.map((book) => apiClient.deleteOriginalBook(book.id))
      )
      setShowDeleteDialog(false)
      setSelectedBooks([])
      await loadBooks()
    } catch (error) {
      console.error('Failed to delete books:', error)
      alert('Failed to delete some books. Please try again.')
    } finally {
      setDeleting(false)
    }
  }

  const handleRecalculatePages = useCallback(async (e: React.MouseEvent, bookId: string) => {
    e.preventDefault()
    e.stopPropagation()
    
    setRecalculatingSingleBook(bookId)
    try {
      const result = await apiClient.recalculateBookPages(bookId)
      if (result.success) {
        await loadBooks()
      }
    } catch (error) {
      console.error('Failed to recalculate pages:', error)
      alert('Failed to recalculate pages')
    } finally {
      setRecalculatingSingleBook(null)
    }
  }, [])

  const handleBulkRecalculatePages = async () => {
    if (selectedBooks.length === 0) return

    setRecalculatingPages(true)
    try {
      const result = await apiClient.bulkRecalculateBookPages(selectedBooks.map(b => b.id))
      if (result.success) {
        const msg = `Updated ${result.updated_count} book(s)` + 
          (result.failed_count > 0 ? `, ${result.failed_count} failed` : '')
        alert(msg)
        setSelectedBooks([])
        await loadBooks()
      }
    } catch (error) {
      console.error('Failed to recalculate pages:', error)
      alert('Failed to recalculate pages for some books')
    } finally {
      setRecalculatingPages(false)
    }
  }

  const columns: ColumnDef<Book>[] = useMemo(() => [
    createSelectionColumn<Book>(),
    {
      accessorKey: 'title_international',
      header: ({ column }) => <SortableHeader column={column}>Title</SortableHeader>,
      cell: ({ row }) => (
        <div className="book-title-cell max-w-md">
          <a 
            href={`/admin/books/${row.original.id}?returnTab=books`}
            className="font-medium text-foreground line-clamp-1 hover:text-primary cursor-pointer block"
            onClick={(e) => {
              // Save scroll position BEFORE navigation
              const scrollTop = window.scrollY || document.documentElement.scrollTop
              sessionStorage.setItem('scroll-position-admin-books-table', String(scrollTop))
              // Only use SPA navigation for left clicks without modifiers
              if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
                e.preventDefault()
                navigate(`/admin/books/${row.original.id}?returnTab=books`)
              }
            }}
          >
            {row.original.title_international}
          </a>
          <div className="text-sm text-muted-foreground line-clamp-1">
            {row.original.author_international}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'cover_url',
      header: 'Cover',
      cell: ({ row }) => (
        <div className="book-cover-cell flex items-center justify-center w-[40px]">
          {row.original.cover_url ? (
            <img 
              src={row.original.cover_url}
              alt={`${row.original.title_international} cover`}
              className="h-[40px] w-auto object-contain rounded border border-border"
            />
          ) : (
            <div className="h-[40px] w-[40px] flex items-center justify-center bg-muted rounded border border-border">
              <BookImage className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'original_language',
      header: ({ column }) => (
        <FilterableHeader 
          column={column} 
          options={LANGUAGE_CODES}
          selectedValues={selectedLanguages}
          onFilterChange={setSelectedLanguages}
          optionLabels={Object.fromEntries(ALL_LANGUAGES.map(l => [l.code, `${l.flag} ${l.name}`]))}
        >
          Language
        </FilterableHeader>
      ),
      cell: ({ row }) => (
        <Badge variant="outline" className="language-badge">
          {row.original.original_language || 'N/A'}
        </Badge>
      ),
    },
    {
      accessorKey: 'genre',
      header: ({ column }) => (
        <FilterableHeader 
          column={column} 
          options={availableGenres}
          selectedValues={selectedGenres}
          onFilterChange={setSelectedGenres}
        >
          Genre
        </FilterableHeader>
      ),
      cell: ({ row }) => (
        <span className="genre-text text-sm">
          {row.original.genre || 'N/A'}
        </span>
      ),
    },
    {
      accessorKey: 'estimated_pages',
      header: ({ column }) => <SortableHeader column={column}>Pages</SortableHeader>,
      cell: ({ row }) => (
        <div className="pages-cell flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">
            {row.original.estimated_pages ?? '—'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'translation_count',
      header: ({ column }) => <SortableHeader column={column}>Translations</SortableHeader>,
      cell: ({ row }) => (
        <div className="translation-count-cell flex items-center gap-2">
          <Languages className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">
            {row.original.translation_count || 0}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'is_hidden',
      header: ({ column }) => (
        <FilterableHeader 
          column={column} 
          options={BOOK_STATUSES}
          selectedValues={selectedStatuses}
          onFilterChange={setSelectedStatuses}
          sortable={false}
        >
          Status
        </FilterableHeader>
      ),
      cell: ({ row }) => (
        <Badge variant={row.original.is_hidden ? 'secondary' : 'default'} className="status-badge">
          {row.original.is_hidden ? 'Hidden' : 'Visible'}
        </Badge>
      ),
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => <SortableHeader column={column}>Created</SortableHeader>,
      cell: ({ row }) => {
        const { date, time } = formatDateTimeCompact(row.original.created_at)
        return (
          <div className="datetime-cell text-sm">
            <div>{date}</div>
            <div className="text-muted-foreground">{time}</div>
          </div>
        )
      },
    },
    {
      accessorKey: 'updated_at',
      header: ({ column }) => <SortableHeader column={column}>Updated</SortableHeader>,
      cell: ({ row }) => {
        const { date, time } = formatDateTimeCompact(row.original.updated_at)
        return (
          <div className="datetime-cell text-sm">
            <div>{date}</div>
            <div className="text-muted-foreground">{time}</div>
          </div>
        )
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="actions-cell flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => handleToggleVisibility(e, row.original.id, row.original.is_hidden)}
            title={row.original.is_hidden ? "Show in public library" : "Hide from public library"}
          >
            {row.original.is_hidden ? (
              <Eye className="h-4 w-4" />
            ) : (
              <EyeOff className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => handleRecalculatePages(e, row.original.id)}
            disabled={recalculatingSingleBook === row.original.id}
            title="Recalculate Pages"
          >
            <RefreshCw className={`h-4 w-4 ${recalculatingSingleBook === row.original.id ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/admin/books/${row.original.id}/edit`)}
            title="Edit Book"
          >
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      ),
      enableSorting: false,
    },
  ], [navigate, handleToggleVisibility, handleRecalculatePages, recalculatingSingleBook, selectedLanguages, selectedGenres, selectedStatuses, availableGenres])

  if (loading) {
    return (
      <div className="books-table-loading flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading books...</p>
      </div>
    )
  }

  return (
    <div className="books-table-container space-y-4">
      {/* Bulk Actions */}
      {selectedBooks.length > 0 && (
        <div className="bulk-actions-bar flex items-center gap-4 p-4 bg-muted/50 rounded-lg border border-border">
          <div className="selection-count flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">
              {selectedBooks.length} book{selectedBooks.length > 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="bulk-action-buttons flex gap-2 ml-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkRecalculatePages}
              disabled={recalculatingPages}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${recalculatingPages ? 'animate-spin' : ''}`} />
              {recalculatingPages ? 'Recalculating...' : 'Recalculate Pages'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowGenerateCoverDialog(true)}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Generate AI Cover
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowGenerateDescriptionDialog(true)}
            >
              <FileText className="mr-2 h-4 w-4" />
              Generate AI Description
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowGenerateDescriptionCoverDialog(true)}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Description+Cover
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowTranslationDialog(true)}
            >
              <Languages className="mr-2 h-4 w-4" />
              Add Translations
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Selected
            </Button>
          </div>
        </div>
      )}

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={books}
        getRowId={(row) => row.id}
        onRowSelectionChange={setSelectedBooks}
        searchPlaceholder="Search by title or author..."
        enableRowSelection={true}
        enableColumnVisibility={true}
        enablePagination={true}
        manualPagination={true}
        pageCount={pageCount}
        pagination={pagination}
        onPaginationChange={setPagination}
        manualSorting={true}
        sorting={sorting}
        onSortingChange={setSorting}
        manualSearch={true}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        tableId="admin-books"
        toolbarActions={
          <div className="toolbar-buttons flex gap-2">
            <Button onClick={() => navigate('/admin/books/bulk-upload')} variant="outline" size="sm">
              <Upload className="mr-2 h-4 w-4" />
              Bulk Upload
            </Button>
            <Button onClick={() => navigate('/admin/books/new')} variant="default" size="sm">
              <Plus className="mr-2 h-4 w-4" />
              Add Book
            </Button>
          </div>
        }
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedBooks.length} book{selectedBooks.length > 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the selected books
              and all their translations from the library.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSelected}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Translation Dialog */}
      {showTranslationDialog && (
        <BulkTranslationDialog
          books={selectedBooks}
          onClose={() => setShowTranslationDialog(false)}
          onSuccess={() => {
            setShowTranslationDialog(false)
            setSelectedBooks([])
            loadBooks()
          }}
        />
      )}

      {/* Generate Cover Dialog */}
      {showGenerateCoverDialog && (
        <GenerateCoverDialog
          books={selectedBooks}
          onClose={() => setShowGenerateCoverDialog(false)}
          onSuccess={() => {
            setShowGenerateCoverDialog(false)
            setSelectedBooks([])
            loadBooks()
          }}
        />
      )}

      {/* Generate Description Dialog */}
      {showGenerateDescriptionDialog && (
        <GenerateDescriptionDialog
          books={selectedBooks}
          onClose={() => setShowGenerateDescriptionDialog(false)}
          onSuccess={() => {
            setShowGenerateDescriptionDialog(false)
            setSelectedBooks([])
            loadBooks()
          }}
        />
      )}

      {/* Generate Description + Cover Dialog */}
      {showGenerateDescriptionCoverDialog && (
        <GenerateDescriptionCoverDialog
          books={selectedBooks}
          onClose={() => setShowGenerateDescriptionCoverDialog(false)}
          onSuccess={() => {
            setShowGenerateDescriptionCoverDialog(false)
            setSelectedBooks([])
            loadBooks()
          }}
        />
      )}
    </div>
  )
}

